// Dok 55 – Samlad byggherrerapport: delegeringskedja (Dok 51–52) och
// checklistrubriker per skede (Dok 53) samt uppgiftsfördelning (Dok 54).
import type { Paragraph, Table as DocxTable } from "docx";
import { supabase } from "@/integrations/supabase/client";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, h1, h2, table, fmtDate } from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";
import {
  BYGGHERRE_CHECKLIST_PHASES,
  BYGGHERRE_CHECKLIST_STATUS_LABELS,
  DELEGATION_LETTER_STATUS_LABELS,
} from "@/lib/basProcess/byggherreDocs";

const safe = (name?: string) => (name || "projekt").replace(/[^\w-]+/g, "_");

export interface ReportLetter {
  id: string;
  from_name: string | null;
  from_role: string | null;
  to_name: string | null;
  to_role: string | null;
  deputy_name: string | null;
  may_delegate_further: boolean | null;
  valid_from: string | null;
  valid_to: string | null;
  signed_at: string | null;
  status: string;
}

export interface ReportChecklistItem {
  phase: string;
  activity: string;
  requirement: string | null;
  status: string;
  responsible: string | null;
  note: string | null;
  due_date: string | null;
  required: boolean | null;
}

export interface ReportDuty {
  task: string;
  representative: string | null;
  handler: string | null;
  basp: string | null;
  basu: string | null;
}

export interface ByggherreReportData {
  projectName: string;
  orderSections: Record<string, string> | null;
  letters: ReportLetter[];
  items: ReportChecklistItem[];
  duties: ReportDuty[];
}

/** Hämtar allt underlag som rapporten bygger på. */
export const loadByggherreReportData = async (
  projectId: string
): Promise<ByggherreReportData> => {
  const [proj, order, letters, items, duties] = await Promise.all([
    supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    supabase
      .from("delegation_orders")
      .select("sections")
      .eq("project_id", projectId)
      .maybeSingle(),
    supabase
      .from("delegation_letters")
      .select(
        "id, from_name, from_role, to_name, to_role, deputy_name, may_delegate_further, valid_from, valid_to, signed_at, status"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: true }),
    supabase
      .from("byggherre_checklist_items")
      .select("phase, activity, requirement, status, responsible, note, due_date, required")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true }),
    supabase
      .from("duty_assignments")
      .select("task, representative, handler, basp, basu")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true }),
  ]);

  return {
    projectName: (proj.data as { name?: string } | null)?.name ?? "",
    orderSections:
      ((order.data as { sections?: Record<string, string> } | null)?.sections as
        | Record<string, string>
        | undefined) ?? null,
    letters: ((letters.data as unknown as ReportLetter[] | null) ?? []),
    items: ((items.data as unknown as ReportChecklistItem[] | null) ?? []),
    duties: ((duties.data as unknown as ReportDuty[] | null) ?? []),
  };
};

const statusLabel = (s: string) => BYGGHERRE_CHECKLIST_STATUS_LABELS[s] || s;

const summarize = (items: ReportChecklistItem[]) => {
  const done = items.filter((i) => i.status === "ok" || i.status === "ej_aktuell").length;
  const notOk = items.filter((i) => i.status === "ej_ok").length;
  const openRequired = items.filter(
    (i) =>
      i.required &&
      !(i.status === "ok" || (i.status === "ej_aktuell" && (i.note || "").trim().length >= 5))
  ).length;
  return { done, notOk, openRequired, total: items.length };
};

/** Dok 55 – samlad rapport som Word-dokument. */
export const generateByggherreReportWordDocument = async (data: ByggherreReportData) => {
  const documentName = "Samlad byggherrerapport – delegering och lagefterlevnad";
  const projectName = data.projectName;
  const stamp = await createStampForCurrentUser({ docNumber: "55", documentName, projectName });
  const footer = await documentFooter({ docNumber: "55", documentType: "byggherre_rapport" });
  const sum = summarize(data.items);

  const children: (Paragraph | DocxTable)[] = [
    body(
      "Rapporten samlar byggherrens delegeringskedja och lagefterlevnad i ett dokument. Underlaget hämtas automatiskt från delegeringsordningen (Dok 51), delegeringsbreven (Dok 52), checklistan (Dok 53) och uppgiftsfördelningen (Dok 54)."
    ),
    h1("1. Sammanfattning"),
    table(
      ["Nyckeltal", "Värde"],
      [
        ["Delegeringsbrev totalt", String(data.letters.length)],
        [
          "Undertecknade delegeringsbrev",
          String(data.letters.filter((l) => l.status === "undertecknat").length),
        ],
        ["Checklistpunkter", String(sum.total)],
        ["Kvitterade (OK eller N/A)", String(sum.done)],
        ["Avvikelser (EJ OK)", String(sum.notOk)],
        ["Öppna obligatoriska punkter", String(sum.openRequired)],
      ]
    ),
    h1("2. Delegeringskedja"),
  ];

  if (data.orderSections?.chain) {
    children.push(h2("Delegeringsordning (Dok 51)"), body(data.orderSections.chain));
  }

  children.push(
    data.letters.length
      ? table(
          [
            "Fördelas av",
            "Fördelas till",
            "Ställföreträdare",
            "Får fördela vidare",
            "Gäller från",
            "Gäller till",
            "Undertecknat",
            "Status",
          ],
          data.letters.map((l) => [
            [l.from_name, l.from_role].filter(Boolean).join(", "),
            [l.to_name, l.to_role].filter(Boolean).join(", "),
            l.deputy_name || "–",
            l.may_delegate_further ? "Ja" : "Nej",
            fmtDate(l.valid_from),
            l.valid_to ? fmtDate(l.valid_to) : "Tills vidare",
            fmtDate(l.signed_at),
            DELEGATION_LETTER_STATUS_LABELS[l.status] || l.status,
          ])
        )
      : body("Inga delegeringsbrev är registrerade i projektet.")
  );

  children.push(h1("3. Lagefterlevnad per skede"));
  BYGGHERRE_CHECKLIST_PHASES.forEach((phase) => {
    const rows = data.items.filter((i) => i.phase === phase.key);
    children.push(h2(phase.label));
    if (!rows.length) {
      children.push(body("Inga punkter registrerade för skedet."));
      return;
    }
    children.push(
      table(
        ["Aktivitet", "Krav", "Obligatorisk", "Ansvarig", "Status", "Kommentar"],
        rows.map((r) => [
          r.activity,
          r.requirement,
          r.required ? "Ja" : "Nej",
          r.responsible,
          statusLabel(r.status),
          [r.note, r.due_date ? `Senast ${fmtDate(r.due_date)}` : null].filter(Boolean).join(" · "),
        ])
      )
    );
  });

  if (data.duties.length) {
    children.push(
      h1("4. Uppgiftsfördelning"),
      table(
        ["Arbetsuppgift", "Representant", "Handläggare", "Bas-P", "Bas-U"],
        data.duties.map((d) => [d.task, d.representative, d.handler, d.basp, d.basu])
      )
    );
  }

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "55", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok55_Byggherrerapport_${safe(projectName)}.docx`, {
    documentType: "byggherre_rapport",
    docNumber: "55",
    documentName,
    snapshot: data,
    stamp,
  });
};

/** Dok 55 – samma rapport som PDF. */
export const generateByggherreReportPdf = async (data: ByggherreReportData) => {
  const [{ default: pdfMake }, fonts] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts"),
  ]);
  const vfs =
    (fonts as unknown as { pdfMake?: { vfs?: Record<string, string> }; vfs?: Record<string, string> })
      .pdfMake?.vfs ??
    (fonts as unknown as { vfs?: Record<string, string> }).vfs;
  if (vfs) (pdfMake as unknown as { vfs: Record<string, string> }).vfs = vfs;

  const sum = summarize(data.items);
  const tableLayout = { fillColor: (row: number) => (row === 0 ? "#EEF2F7" : null) };

  const content: unknown[] = [
    { text: "Dok 55 – Samlad byggherrerapport", style: "title" },
    { text: data.projectName || "Projekt", style: "sub" },
    {
      text: `Skapad ${new Date().toLocaleDateString("sv-SE")} · delegeringskedja och lagefterlevnad enligt AFS 2023:3 kap. 2`,
      style: "muted",
      margin: [0, 0, 0, 12],
    },
    { text: "1. Sammanfattning", style: "h1" },
    {
      table: {
        widths: ["*", 80],
        body: [
          ["Nyckeltal", "Värde"],
          ["Delegeringsbrev totalt", String(data.letters.length)],
          [
            "Undertecknade delegeringsbrev",
            String(data.letters.filter((l) => l.status === "undertecknat").length),
          ],
          ["Checklistpunkter", String(sum.total)],
          ["Kvitterade (OK eller N/A)", String(sum.done)],
          ["Avvikelser (EJ OK)", String(sum.notOk)],
          ["Öppna obligatoriska punkter", String(sum.openRequired)],
        ],
      },
      layout: tableLayout,
      margin: [0, 0, 0, 12],
    },
    { text: "2. Delegeringskedja", style: "h1" },
  ];

  if (data.letters.length) {
    content.push({
      table: {
        headerRows: 1,
        widths: ["*", "*", 70, 60, 60, 60],
        body: [
          ["Fördelas av", "Fördelas till", "Ställföreträdare", "Från", "Till", "Status"],
          ...data.letters.map((l) => [
            [l.from_name, l.from_role].filter(Boolean).join(", ") || "–",
            [l.to_name, l.to_role].filter(Boolean).join(", ") || "–",
            l.deputy_name || "–",
            l.valid_from ? fmtDate(l.valid_from) : "–",
            l.valid_to ? fmtDate(l.valid_to) : "Tills vidare",
            DELEGATION_LETTER_STATUS_LABELS[l.status] || l.status,
          ]),
        ],
      },
      layout: tableLayout,
      fontSize: 9,
      margin: [0, 0, 0, 12],
    });
  } else {
    content.push({ text: "Inga delegeringsbrev är registrerade i projektet.", style: "muted" });
  }

  content.push({ text: "3. Lagefterlevnad per skede", style: "h1" });
  BYGGHERRE_CHECKLIST_PHASES.forEach((phase) => {
    const rows = data.items.filter((i) => i.phase === phase.key);
    content.push({ text: phase.label, style: "h2" });
    if (!rows.length) {
      content.push({ text: "Inga punkter registrerade för skedet.", style: "muted" });
      return;
    }
    content.push({
      table: {
        headerRows: 1,
        widths: ["*", 55, 70, 60, "*"],
        body: [
          ["Aktivitet", "Oblig.", "Ansvarig", "Status", "Kommentar"],
          ...rows.map((r) => [
            r.activity,
            r.required ? "Ja" : "Nej",
            r.responsible || "–",
            statusLabel(r.status),
            r.note || "–",
          ]),
        ],
      },
      layout: tableLayout,
      fontSize: 9,
      margin: [0, 0, 0, 10],
    });
  });

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [40, 45, 40, 50],
    content,
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 2] },
      sub: { fontSize: 12, bold: true, margin: [0, 0, 0, 2] },
      h1: { fontSize: 13, bold: true, margin: [0, 10, 0, 6] },
      h2: { fontSize: 11, bold: true, margin: [0, 8, 0, 4] },
      muted: { fontSize: 9, color: "#666666" },
    },
    defaultStyle: { fontSize: 10 },
    footer: (page: number, count: number) => ({
      text: `Dok 55 · ${data.projectName || "Projekt"} · sida ${page} av ${count}`,
      style: "muted",
      alignment: "center",
      margin: [0, 12, 0, 0],
    }),
  };

  (pdfMake as unknown as { createPdf: (d: unknown) => { download: (n: string) => void } })
    .createPdf(docDefinition)
    .download(`Dok55_Byggherrerapport_${safe(data.projectName)}.pdf`);
};
